import { datadogRum } from '@datadog/browser-rum';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { logHandler } from 'src/utils/helpers';

import configContext from '../config-context';
import { useConfig } from '../hooks';

import { AUTHENTICATION_TYPES, type AuthInfo } from './authTypes';
import { getUser } from './getUser';

const initialAuthState = {
  authenticationStatus: AUTHENTICATION_TYPES.AUTH_PENDING,
  emailAddress: '',
  firstName: '',
  lastName: '',
  profilePhotoUrl: undefined,
};
const AuthContext = createContext<AuthInfo>(initialAuthState);

const unauthenticatedAuthInfo = {
  authenticationStatus: AUTHENTICATION_TYPES.UNAUTHENTICATED,
  emailAddress: '',
  firstName: '',
  lastName: '',
  profilePhotoUrl: undefined,
  id: undefined,
} as const;

export const AuthProvider = ({
  authBypass,
  children,
}: {
  authBypass: boolean;
  children: ReactNode;
}) => {
  const { useMockedAuth } = useContext(configContext);

  const { site, country } = useConfig();

  const [authStatus, setAuthStatus] = useState<AuthInfo>(initialAuthState);

  useEffect(() => {
    // Skip user auth check if already authenticated or authBypass is true
    if (
      authStatus.authenticationStatus === AUTHENTICATION_TYPES.AUTHENTICATED ||
      authBypass
    ) {
      return;
    }

    getUser({ useAuth: true }, useMockedAuth, site, country.toUpperCase())
      .then((data: Awaited<ReturnType<typeof getUser>>) => {
        if (!data || !('viewer' in data) || !data.viewer) {
          setAuthStatus({
            ...unauthenticatedAuthInfo,
            error: 'No data returned from getUser',
          });
          return;
        }

        const {
          viewer: {
            id,
            emailAddress,
            personalDetails: { firstName, lastName },
            profileAvatar,
          },
          tags,
        } = data;
        setAuthStatus({
          error: undefined,
          authenticationStatus: AUTHENTICATION_TYPES.AUTHENTICATED,
          emailAddress,
          firstName,
          lastName,
          profilePhotoUrl: profileAvatar?.image128Url,
          id,
          tags,
        });
      })
      .catch((err: any) => {
        datadogRum.addError(err as Error, {
          feature: 'AuthProvider.getUser',
        });
        logHandler(err);
        setAuthStatus({ ...unauthenticatedAuthInfo, error: err });
      });
  }, [
    useMockedAuth,
    authStatus.authenticationStatus,
    authBypass,
    site,
    country,
  ]);

  return (
    <AuthContext.Provider value={authStatus}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
